import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { makeAuthedRequest } from "shared/api/authRequest";
import { AdminTable } from "components/shared/AdminTable";
import { AdminTablePagination } from "components/shared/AdminTablePagination";

type ActivitySource = "Hardware" | "MobileApp";

type LockActivityCategory = "AccessGranted" | "AccessDenied";

type UserActivityItem = {
  id: string;
  guestId?: string;
  fullName?: string;
  guestType?: string;
  isGuest?: boolean;
  lockId?: string;
  lockName?: string;
  siteId?: string;
  siteName?: string;
  timestamp: string;
  lockActivityCategory?: LockActivityCategory;
  credentialType?: string;
  credentialNumber?: string;
  details?: string;
  source?: ActivitySource;
};

type UserActivityResponse = {
  items: UserActivityItem[];
  totalCount: number;
  pageSize: number;
  pageNumber: number;
};

// Enum option arrays for consistent dropdown rendering
const SourceOptions: ActivitySource[] = ["Hardware", "MobileApp"];
const CategoryOptions: LockActivityCategory[] = [
  "AccessGranted",
  "AccessDenied",
];

async function fetchUserActivity(
  params: Record<string, string | number | boolean | undefined>,
) {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") {
      searchParams.append(k, String(v));
    }
  });
  const data = await makeAuthedRequest<UserActivityResponse>({
    method: "get",
    url: `/api/user-activity/search?${searchParams.toString()}`,
  });
  return data;
}

export default function PropertyActivity() {
  const { externalId } = useParams<{ externalId: string }>();

  const [pageNumber, setPageNumber] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(20);

  // Defaults: Source=MobileApp, Category=All (""), GuestType=Resident
  const [source, setSource] = useState<ActivitySource | "">("MobileApp");
  const [category, setCategory] = useState<LockActivityCategory | "">("");
  const [guestType, setGuestType] = useState<"" | "Resident" | "Staff">(
    "Resident",
  );
  const [isResidentGuest, setIsResidentGuest] = useState<boolean | "">("");
  const [siteId, setSiteId] = useState<string>("");

  // Ensure IsResidentGuest only applies when GuestType is Resident
  useEffect(() => {
    if (guestType !== "Resident" && isResidentGuest !== "") {
      setIsResidentGuest("");
    }
  }, [guestType, isResidentGuest]);

  const queryKey = useMemo(
    () => [
      "user-activity",
      externalId,
      pageNumber,
      pageSize,
      source,
      category,
      guestType,
      isResidentGuest,
      siteId,
    ],
    [
      externalId,
      pageNumber,
      pageSize,
      source,
      category,
      guestType,
      isResidentGuest,
      siteId,
    ],
  );

  const { data, isLoading, isError } = useQuery<UserActivityResponse>({
    queryKey,
    queryFn: () =>
      fetchUserActivity({
        PropertyExternalId: externalId,
        Page: pageNumber,
        PageSize: pageSize,
        Source: source || undefined,
        Category: category || undefined,
        GuestType: guestType || undefined,
        IsResidentGuest:
          guestType === "Resident" && isResidentGuest !== ""
            ? Boolean(isResidentGuest)
            : undefined,
        SiteId: siteId || undefined,
      }),
  });

  // Normalize response shape: support `items` or `data` arrays
  const rawItems = (data as any)?.items ?? (data as any)?.data ?? [];
  const items: UserActivityItem[] = rawItems as UserActivityItem[];
  const totalCount = (data as any)?.totalCount ?? items.length ?? 0;
  const isFirstPage = pageNumber === 0;
  const isLastPage =
    totalCount > 0
      ? (pageNumber + 1) * pageSize >= totalCount
      : items.length < pageSize;

  return (
    <AdminTable
      aria-label="Property activity"
      toolbar={
        <div className="flex flex-col gap-4 px-4 py-4 xl:flex-row xl:items-end xl:justify-between">
          <label className="grid w-full gap-1 text-xs font-semibold tracking-wide uppercase text-slate-500 xl:max-w-sm">
            Site ID
            <input
              className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm font-normal normal-case text-slate-800 outline-none transition-colors placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
              placeholder="Filter by site ID"
              value={siteId}
              onChange={(event) => {
                setSiteId(event.target.value);
                setPageNumber(0);
              }}
            />
          </label>
          <div className="flex flex-wrap items-end gap-3">
            <label className="grid gap-1 text-xs font-semibold tracking-wide uppercase text-slate-500">
              Source
              <select
                className="h-10 min-w-32 rounded-md border border-slate-200 bg-white px-3 text-sm font-normal normal-case text-slate-800 outline-none transition-colors focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                value={source}
                onChange={(event) => {
                  setSource(event.target.value as ActivitySource | "");
                  setPageNumber(0);
                }}
              >
                <option value="">All</option>
                {SourceOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </label>
            <label className="grid gap-1 text-xs font-semibold tracking-wide uppercase text-slate-500">
              Category
              <select
                className="h-10 min-w-36 rounded-md border border-slate-200 bg-white px-3 text-sm font-normal normal-case text-slate-800 outline-none transition-colors focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                value={category}
                onChange={(event) => {
                  setCategory(event.target.value as LockActivityCategory | "");
                  setPageNumber(0);
                }}
              >
                <option value="">All</option>
                {CategoryOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </label>
            <label className="grid gap-1 text-xs font-semibold tracking-wide uppercase text-slate-500">
              Guest type
              <select
                className="h-10 min-w-32 rounded-md border border-slate-200 bg-white px-3 text-sm font-normal normal-case text-slate-800 outline-none transition-colors focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                value={guestType}
                onChange={(event) => {
                  setGuestType(event.target.value as "" | "Resident" | "Staff");
                  setPageNumber(0);
                }}
              >
                <option value="">All</option>
                <option value="Resident">Resident</option>
                <option value="Staff">Staff</option>
              </select>
            </label>
            {guestType === "Resident" ? (
              <label className="grid gap-1 text-xs font-semibold tracking-wide uppercase text-slate-500">
                Is resident guest
                <select
                  className="h-10 min-w-36 rounded-md border border-slate-200 bg-white px-3 text-sm font-normal normal-case text-slate-800 outline-none transition-colors focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  value={String(isResidentGuest)}
                  onChange={(event) => {
                    const value = event.target.value;
                    setIsResidentGuest(value === "" ? "" : value === "true");
                    setPageNumber(0);
                  }}
                >
                  <option value="">Any</option>
                  <option value="true">True</option>
                  <option value="false">False</option>
                </select>
              </label>
            ) : null}
          </div>
        </div>
      }
      footer={
        isError ? undefined : (
          <AdminTablePagination
            currentPage={pageNumber}
            pageSize={pageSize}
            pageSizeOptions={[10, 20, 30, 50]}
            visibleRowCount={items.length}
            isLoading={isLoading}
            disablePrevious={isFirstPage}
            disableNext={isLastPage}
            onNext={() => setPageNumber((currentPage) => currentPage + 1)}
            onPrevious={() => setPageNumber((currentPage) => Math.max(0, currentPage - 1))}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPageNumber(0);
            }}
          />
        )
      }
    >
      {isLoading ? (
        <tbody>
          <tr>
            <td colSpan={8}>
              <div className="flex items-center justify-center min-h-80 text-sm text-slate-500">
                Loading activity...
              </div>
            </td>
          </tr>
        </tbody>
      ) : isError ? (
        <tbody>
          <tr>
            <td colSpan={8}>
              <div className="flex items-center justify-center px-4 text-sm text-center min-h-80 text-rose-700">
                There was an error loading activity.
              </div>
            </td>
          </tr>
        </tbody>
      ) : (
        <>
          <thead>
            <tr>
              <th scope="col">Name</th>
              <th scope="col">Site</th>
              <th scope="col">Lock</th>
              <th scope="col">Timestamp</th>
              <th scope="col">Category</th>
              <th scope="col">Source</th>
              <th scope="col">Credentials</th>
              <th scope="col">Details</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id}>
                <td>
                  {item.fullName}
                  {item.guestType || item.isGuest !== undefined ? (
                    <div className="pt-1 text-xs text-slate-500">
                      {[item.guestType, item.isGuest ? "Guest" : undefined]
                        .filter(Boolean)
                        .join(" • ")}
                    </div>
                  ) : null}
                </td>
                <td>
                  {item.siteName}
                  {item.siteId ? (
                    <div className="pt-1 font-mono text-xs text-slate-500">
                      {item.siteId}
                    </div>
                  ) : null}
                </td>
                <td>
                  {item.lockName}
                  {item.lockId ? (
                    <div className="pt-1 font-mono text-xs text-slate-500">
                      {item.lockId}
                    </div>
                  ) : null}
                </td>
                <td className="whitespace-nowrap">
                  {new Date(item.timestamp).toLocaleString()}
                </td>
                <td>{item.lockActivityCategory}</td>
                <td>{item.source}</td>
                <td>
                  <div>Credential Number: {item.credentialNumber}</div>
                  <div>Credential Type: {item.credentialType}</div>
                </td>
                <td>{item.details}</td>
              </tr>
            ))}
            {items.length === 0 ? (
              <tr>
                <td colSpan={8} className="py-16 text-sm text-center text-slate-500">
                  No activity found.
                </td>
              </tr>
            ) : null}
          </tbody>
        </>
      )}
    </AdminTable>
  );
}
